import { useLang } from '../contexts/LanguageContext';
import { ArrowRight, ChevronDown, Phone } from 'lucide-react';

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center bg-[#FAFAFA] overflow-hidden pt-16"
    >
      {/* Background grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(#09090B 1px, transparent 1px), linear-gradient(90deg, #09090B 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-0 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-4rem)]">
          {/* Left content */}
          <div className="flex flex-col justify-center space-y-8 lg:py-24">
            <div>
              <span
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#C2410C] mb-6"
                data-testid="hero-eyebrow"
              >
                <span className="w-6 h-px bg-[#C2410C]" />
                {t('hero_eyebrow')}
              </span>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 leading-[1.08] tracking-tight"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="hero-headline"
              >
                {t('hero_headline')}
              </h1>
            </div>

            <p
              className="text-lg text-zinc-500 leading-relaxed max-w-lg"
              data-testid="hero-subheadline"
            >
              {t('hero_subheadline')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo('#contacto')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold rounded-full transition-all duration-200 active:scale-95 shadow-lg shadow-orange-900/20 text-base"
                data-testid="hero-cta-primary"
              >
                {t('hero_cta_primary')}
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() => scrollTo('#portfolio')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-zinc-200 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 font-semibold rounded-full transition-all duration-200 text-base"
                data-testid="hero-cta-secondary"
              >
                {t('hero_cta_secondary')}
              </button>
            </div>

            <a
              href="tel:+351915995141"
              className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#C2410C] text-sm font-medium transition-colors"
              data-testid="hero-phone-link"
            >
              <Phone size={14} />
              +351 915 995 141
            </a>

            {/* Trust stats */}
            <div className="flex gap-8 pt-4 border-t border-zinc-200">
              {[
                { val: t('hero_trust_1'), sub: t('hero_trust_1_sub') },
                { val: t('hero_trust_2'), sub: t('hero_trust_2_sub') },
                { val: t('hero_trust_3'), sub: t('hero_trust_3_sub') },
              ].map((item, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <div
                    className="text-2xl font-bold text-zinc-900"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {item.val}
                  </div>
                  <div className="text-xs text-zinc-500 font-medium mt-0.5">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right image */}
          <div className="relative lg:py-16 flex items-center justify-center">
            <div className="relative w-full max-w-lg lg:max-w-none">
              {/* Decorative border frame */}
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#C2410C]/20 rounded-2xl" />
              <div className="relative overflow-hidden rounded-2xl shadow-2xl aspect-[4/5] lg:aspect-[3/4]">
                <img
                  src="/images/cozinha.webp"
                  alt="Montagem de cozinha profissional"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/30 via-transparent to-transparent" />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-xl px-5 py-4 border border-zinc-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#C2410C]/10 rounded-full flex items-center justify-center">
                    <span className="text-[#C2410C] text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>✓</span>
                  </div>
                  <div>
                    <div className="text-xs font-bold text-zinc-900">Qualidade Garantida</div>
                    <div className="text-xs text-zinc-500">Cascais, Amadora, Sintra, Oeiras, Odivelas, Loures e Lisboa</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => scrollTo('#servicos')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-400 hover:text-zinc-700 transition-colors animate-bounce"
        data-testid="hero-scroll-down"
      >
        <ChevronDown size={24} />
      </button>
    </section>
  );
}
